import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Divider,
  Link,
  Grid,
  Stack,
  IconButton,
  Breadcrumbs,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigation } from "contexts/notificationsContext";
import { DashboardStackCustom, StackCustom } from "components/StackCustom/styles";
import {
  IComponente,
  IComponenteCor,
  ISelectOption,
  ComponenteTabIndex,
  SITUACAO_OPTIONS,
  STATUS_OPTIONS,
} from "./types/componente.types";
import {
  obterComponentePorId,
  salvarComponente,
  excluirComponente,
  duplicarComponente,
  listarReferencias,
  listarLinhas,
  listarGrupos,
  listarCores,
  listarUnidades,
  obterCoresComponente,
  salvarCoresComponente,
  validarCodigoUnico,
} from "./services/componenteService";
import { useValidacaoCodigoUnico } from "../hooks/useValidacaoCodigoUnico";
import { useSomenteLeituraN1 } from "../hooks/useSomenteLeituraN1";
import { AvisoPlanilhaPendenteN1 } from "../components/AvisoPlanilhaPendenteN1";
import { useFormularioAlterado } from "../hooks/useFormularioAlterado";
import { TabGeral } from "./components/TabGeral";
import { TabCores } from "./components/TabCores";
import { ModalGerenciamentoMidias } from "./components/ModalGerenciamentoMidias";
import { ModalConfirmacaoExcluir } from "./components/ModalConfirmacaoExcluir";
import { ModalHistorico } from "./components/ModalHistorico";

// FASE 05 — S3/PO 2026-05-13: IdTipoTela do Componente (enum TipoTela = 2).
const ID_TIPO_TELA_COMPONENTE = 2;

// Helper para formatar data de atualização
const formatarDataAtualizacao = (data?: string) => {
  if (!data) return "";
  try {
    const date = new Date(data);
    return `${date.toLocaleDateString("pt-BR")} - ${date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch (e) {
    console.error("Erro ao formatar data:", e);
    return "";
  }
};

export const CadastroComponente: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const idComponenteParam = params?.id;
  const idComponente = idComponenteParam && idComponenteParam !== "novo" ? parseInt(idComponenteParam) : undefined;
  const isEdicao = !!idComponente;

  const { openNotification } = useNavigation();

  // FASE 05 — 2ª validação/PO 2026-07-24 (B4): empresa "apenas consulta" abre a tela
  // em somente-leitura (campos bloqueados, sem botões de gravar/duplicar/excluir).
  // FASE 05 — 6ª validação/PO 2026-08-11: bloqueia edição também quando o MODO EDIÇÃO está desligado.
  // Plano 14/10/2026 (Frente 4): planilha de componentes em andamento também deixa a tela somente-leitura.
  const somenteLeitura = useSomenteLeituraN1("componentes");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabIndex, setTabIndex] = useState<ComponenteTabIndex>(0);
  const [componente, setComponente] = useState<Partial<IComponente>>({
    codigo: "",
    descricao: "",
    idSituacao: 1, // Em edição
    idStatus: 1, // Ativo
    aplicacao: "cor_unica", // Default: Cor única
    // Não inicializar com 0 - deixar undefined para que o usuário selecione
    quantidadeEmbalagem: 1,
    loteMinimoQtdEmbalagens: 1, // Default 1
  });

  // Cores separadas para melhor controle
  const [cores, setCores] = useState<IComponenteCor[]>([]);

  // FASE 05 — Ajustes 25/08 (item 4): SALVAR só habilita depois de alguma alteração.
  // Em cadastro novo não há estado inicial a comparar — o hook devolve sempre habilitado.
  // Recarrega o cadastro após salvar, e o hook rearma o retrato sozinho — daí só `alterado`.
  const { alterado } = useFormularioAlterado(
    { componente, cores },
    { ehNovo: !isEdicao, pronto: !loading }
  );

  // Listas de referência
  const [referencias, setReferencias] = useState<ISelectOption[]>([]);
  const [linhas, setLinhas] = useState<ISelectOption[]>([]);
  const [grupos, setGrupos] = useState<ISelectOption[]>([]);
  const [coresLista, setCoresLista] = useState<ISelectOption[]>([]);
  const [unidades, setUnidades] = useState<ISelectOption[]>([]);

  // Modais
  const [modalMidiasAberto, setModalMidiasAberto] = useState(false);
  const [modalExcluirAberto, setModalExcluirAberto] = useState(false);
  const [modalHistoricoAberto, setModalHistoricoAberto] = useState(false);

  // FASE 05 — S3/PO 2026-05-13: carrega apenas as listas globais ao montar.
  // Referencias passa idTipoTela=2 (MaterialComponente) para filtrar pela empresa.
  // Linhas e carregada via useEffect quando idReferencia mudar.
  const carregarListasReferencia = useCallback(async () => {
    try {
      const [
        refsData,
        gruposData,
        coresData,
        unidadesData,
      ] = await Promise.all([
        listarReferencias(ID_TIPO_TELA_COMPONENTE),
        listarGrupos(),
        listarCores(),
        listarUnidades(),
      ]);

      setReferencias(refsData);
      setGrupos(gruposData);
      setCoresLista(coresData);
      setUnidades(unidadesData);
    } catch (error) {
      console.error("Erro ao carregar listas de referência:", error);
      openNotification({
        message: "Erro ao carregar listas de referência.",
        type: "error",
      });
    }
  }, [openNotification]);

  // Carregar dados do componente (se edição)
  const carregarComponente = useCallback(async () => {
    if (!idComponente) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const componenteData = await obterComponentePorId(idComponente);
      
      if (componenteData) {
        setComponente(componenteData);
        // Se aplicacao = 'colorido', usar cores do componente (já vêm do backend)
        // Se não vierem, tentar buscar separadamente (compatibilidade)
        if (componenteData.aplicacao === "colorido") {
          if (componenteData.cores && componenteData.cores.length > 0) {
            setCores(componenteData.cores);
          } else {
            // Fallback: buscar cores separadamente se não vieram no componente
            const coresData = await obterCoresComponente(idComponente);
            setCores(coresData || []);
          }
        } else {
          setCores([]);
        }
      } else {
        openNotification({ message: "Componente não encontrado", type: "error" });
        navigate("/produtosN1");
      }
    } catch (error) {
      console.error("Erro ao carregar componente:", error);
      openNotification({ message: "Erro ao carregar componente", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [idComponente, navigate, openNotification]);

  useEffect(() => {
    carregarListasReferencia();
    if (isEdicao) {
      carregarComponente();
    } else {
      setLoading(false);
    }
  }, [carregarListasReferencia, isEdicao]);
  
  // Carregar componente quando idComponente mudar
  useEffect(() => {
    if (idComponente) {
      carregarComponente();
    }
  }, [idComponente, carregarComponente]);

  // FASE 05 — S3+/PO 2026-05-13: validacao em tempo real de codigo duplicado.
  const { codigoDuplicado } = useValidacaoCodigoUnico(
    componente.codigo,
    idComponente,
    validarCodigoUnico
  );

  // FASE 05 — S3/PO 2026-05-13: cascata Referencia -> Linha.
  // Recarrega o combo Linha sempre que a Referencia selecionada muda.
  useEffect(() => {
    if (componente.idReferencia) {
      listarLinhas(componente.idReferencia).then(setLinhas);
    } else {
      setLinhas([]);
    }
  }, [componente.idReferencia]);

  // Handlers
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue as ComponenteTabIndex);
  };

  // FASE 05 — S3/PO 2026-05-13: limpar Linha quando Referencia muda.
  const handleComponenteChange = (campo: keyof IComponente, valor: any) => {
    setComponente((prev) => {
      const next: Partial<IComponente> = { ...prev, [campo]: valor };
      if (campo === "idReferencia") {
        next.idLinha = undefined;
      }
      return next;
    });

    // Regra condicional: quando Aplicação muda, ajustar visibilidade da aba Cores
    if (campo === 'aplicacao') {
      if (valor === 'cor_unica') {
        // Se mudou para Cor única, ocultar aba Cores (não remover dados, apenas ocultar)
        // A aba já será oculta pela lógica de renderização
      } else if (valor === 'colorido') {
        // Se mudou para Colorido, garantir que há pelo menos uma cor
        if (cores.length === 0) {
          // Não adicionar automaticamente - usuário deve adicionar manualmente
        }
      }
    }
  };

  const handleSalvar = async () => {
    // Validações
    if (!componente.codigo?.trim()) {
      openNotification({ message: "Código é obrigatório", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!componente.descricao?.trim()) {
      openNotification({ message: "Descrição é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }

    // FASE 05 — S3+/PO 2026-05-13: bloquear save se codigo ja cadastrado.
    if (codigoDuplicado) {
      // FASE 05 — 2ª validação/PO 2026-07-24 (D6): mensagem oficial do PO.
      openNotification({
        message: "Este código de produto já existe. Informe um código diferente ou inclua o sufixo da sua empresa ou entre em contato com o departamento de Produto Informação da Alumisoft.",
        type: "warning",
      });
      setTabIndex(0);
      return;
    }

    // Validar campos obrigatórios
    if (!componente.idReferencia || componente.idReferencia < 1) {
      openNotification({ message: "Referência é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!componente.idLinha || componente.idLinha < 1) {
      openNotification({ message: "Linha é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!componente.idGrupo || componente.idGrupo < 1) {
      openNotification({ message: "Grupo é obrigatório", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!componente.idUnidadeItem || componente.idUnidadeItem < 1) {
      openNotification({ message: "Unidade item é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (!componente.idUnidadeEmbalagem || componente.idUnidadeEmbalagem < 1) {
      openNotification({ message: "Unidade embalagem é obrigatória", type: "warning" });
      setTabIndex(0);
      return;
    }
    
    // Validar lote mínimo apenas se estiver definido (pode ser undefined em novos cadastros)
    const loteMinimo = componente.loteMinimoQtdEmbalagens ?? 1; // Default: 1
    if (loteMinimo < 1 || loteMinimo > 99999) {
      openNotification({ message: "Lote mínimo deve ser entre 1 e 99999", type: "warning" });
      setTabIndex(0);
      return;
    }
    
    if (componente.aplicacao === 'cor_unica' && (!componente.idCor || componente.idCor < 1)) {
      openNotification({ message: "Cor é obrigatória quando Aplicação = Cor única", type: "warning" });
      setTabIndex(0);
      return;
    }
    if (componente.aplicacao === 'colorido' && cores.length === 0) {
      openNotification({ message: "Adicione pelo menos uma cor quando Aplicação = Colorido", type: "warning" });
      setTabIndex(1);
      return;
    }

    setSaving(true);
    try {
      const dadosParaSalvar: Partial<IComponente> = {
        ...componente,
        loteMinimoQtdEmbalagens: loteMinimo, // Garantir que sempre tenha um valor
        // FASE 05 — 2ª validação/PO 2026-07-24 (D1): qualquer inclusão/alteração grava
        // a situação como "Em edição" (id 1); a homologação é feita depois na listagem.
        idSituacao: 1,
      };

      const resultado = await salvarComponente(dadosParaSalvar);
      if (resultado?.sucesso && resultado.id) {
        // FASE 05 — 2ª validação/PO 2026-07-24 (D8): só exibir "salvo com sucesso" DEPOIS
        // de concluir as cores e a navegação. Se as cores falharem, o serviço já mostra
        // o erro e não exibimos o sucesso — evita "sucesso seguido de erro".
        if (!isEdicao) {
          let coresOk = true;
          if (componente.aplicacao === 'colorido' && cores.length > 0) {
            coresOk = await salvarCoresComponente(resultado.id, cores);
          }
          if (coresOk) {
            openNotification({ message: "Componente salvo com sucesso", type: "success" });
            // Redirecionar para edição — recarrega o componente automaticamente
            navigate(`/produtosN1/componentes/${resultado.id}`, { replace: true });
          }
        } else if (isEdicao && idComponente) {
          // FASE 05 — 6ª validação/PO 2026-08-11 (F3 Etapa 2): o salvar pode ter congelado a
          // versão de mercado e redirecionado a edição para uma NOVA cópia de trabalho. As cores
          // vão para o id RETORNADO — nunca para o registro publicado.
          const coresOk = componente.aplicacao === 'colorido'
            ? await salvarCoresComponente(resultado.id, cores)
            : await salvarCoresComponente(resultado.id, []); // limpar cores em Cor única
          if (coresOk) {
            openNotification({ message: "Componente salvo com sucesso", type: "success" });
            if (String(resultado.id) !== String(idComponente)) {
              navigate(`/produtosN1/componentes/${resultado.id}`, { replace: true });
            } else {
              await carregarComponente();
            }
          }
        }
      } else {
        // Se não teve sucesso ou não retornou ID, mostrar erro
        if (!resultado) {
          openNotification({ 
            message: "Erro ao salvar componente. Verifique os logs do console.", 
            type: "error" 
          });
        } else if (!resultado.sucesso) {
          openNotification({ 
            message: resultado.mensagem || "Erro ao salvar componente", 
            type: "error" 
          });
        } else if (!resultado.id) {
          openNotification({ 
            message: "Componente salvo, mas ID não foi retornado. Recarregue a página.", 
            type: "warning" 
          });
        }
      }
    } catch (error: any) {
      // FASE 05 — 6ª validação/PO 2026-08-04: exibir a mensagem real da API (getErrorsByApi lança).
      console.error("Erro ao salvar:", error);
      openNotification({
        message: error?.message || "Erro ao salvar componente. Tente novamente.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicar = async () => {
    if (!idComponente) return;

    setSaving(true);
    try {
      const resultado = await duplicarComponente(idComponente);
      if (resultado?.sucesso && resultado.id) {
        openNotification({ message: "Componente duplicado com sucesso", type: "success" });
        navigate(`/produtosN1/componentes/${resultado.id}`, { replace: true });
      }
    } catch (error) {
      console.error("Erro ao duplicar:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async () => {
    if (!idComponente) return;

    setSaving(true);
    try {
      const sucesso = await excluirComponente(idComponente);
      if (sucesso) {
        openNotification({ message: "Componente excluído com sucesso", type: "success" });
        navigate("/produtosN1");
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleMidiasSave = (midias: {
    imagemUrl?: string;
    imagemNomeArquivo?: string;
    iconeUrl?: string;
    iconeNomeArquivo?: string;
    fichaTecnicaUrl?: string;
    fichaTecnicaNomeArquivo?: string;
  }, idAlvo?: number) => {
    setComponente((prev) => ({
      ...prev,
      imagemUrl: midias.imagemUrl,
      imagemNomeArquivo: midias.imagemNomeArquivo,
      iconeUrl: midias.iconeUrl,
      iconeNomeArquivo: midias.iconeNomeArquivo,
      fichaTecnicaUrl: midias.fichaTecnicaUrl,
      fichaTecnicaNomeArquivo: midias.fichaTecnicaNomeArquivo,
    }));
    // Plano 21/09/2026 (Frente 3): componente Disponível → a mídia foi para a cópia de trabalho;
    // navegar para ela (mesmo tratamento do handleSalvar quando o espelho cria a cópia).
    if (idAlvo && String(idAlvo) !== String(idComponente)) {
      navigate(`/produtosN1/componentes/${idAlvo}`, { replace: true });
    }
  };

  const renderTabContent = () => {
    switch (tabIndex) {
      case 0: // GERAL
        return (
          <TabGeral
            componente={componente}
            onChange={handleComponenteChange}
            referencias={referencias}
            linhas={linhas}
            grupos={grupos}
            cores={coresLista}
            unidades={unidades}
            onOpenMidias={() => setModalMidiasAberto(true)}
            codigoDuplicado={codigoDuplicado}
          />
        );
      case 1: // CORES (apenas quando aplicacao = 'colorido')
        return (
          <TabCores
            cores={cores}
            onChange={setCores}
            coresLista={coresLista}
            codigoBase={componente.codigo || ""}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // FASE 05 — 2ª validação/PO 2026-07-24 (D9): aba CORES (Colorido) oculta — backlog futuro.
  const mostrarAbaCores = false;

  const handleVoltar = () => {
    navigate("/produtosN1");
  };

  return (
    <DashboardStackCustom sx={{ width: "100%" }} pt={2}>
      <Grid container display={"flex"} alignItems={"center"}>
        {/* Header: Título + Breadcrumb - Fora do StackCustom para ter cor de fundo da página */}
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} sx={{ width: "100%", px: 4.5 }}>
          <IconButton onClick={handleVoltar} size="small" sx={{ color: "#666" }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ color: "#333", fontWeight: 400, fontSize: "1.8rem" }}>
            Cadastro de Componente
          </Typography>
        </Stack>
        
        {/* Breadcrumb abaixo do título */}
        <Box sx={{ 
          borderBottom: "2px solid #3C90AD", 
          pb: 1.5, 
          mb: 2,
          ml: 4.5,
          width: "100%",
        }}>
          <Breadcrumbs separator="›" sx={{ mt: 0 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Home
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/produtosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Cadastros
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => { e.preventDefault(); navigate("/produtosN1"); }}
              sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer" }}
            >
              Componentes
            </Link>
            <Typography color="text.primary" sx={{ fontSize: "0.75rem" }}>
              {isEdicao ? "Editar" : "Novo Componente"}
            </Typography>
          </Breadcrumbs>
        </Box>

        <StackCustom>
          
          {/* Última atualização e Log */}
          {isEdicao && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2, gap: 2 }}>
              {componente.dataUltimaAtualizacao && formatarDataAtualizacao(componente.dataUltimaAtualizacao) && (
                <Typography variant="body2" color="text.secondary">
                  Última atualização: {formatarDataAtualizacao(componente.dataUltimaAtualizacao)}
                </Typography>
              )}
              <Link
                component="button"
                variant="body2"
                onClick={() => setModalHistoricoAberto(true)}
                sx={{ color: "#3C90AD" }}
              >
                Log de atualizações
              </Link>
            </Box>
          )}

          <AvisoPlanilhaPendenteN1 tipoMaterial="componentes" />

          {/* Abas */}
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              mb: 3,
              minHeight: "auto",
              "& .MuiTabs-flexContainer": {
                gap: "2px",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                minHeight: "52px",
                backgroundColor: "#7ab8cc",
                color: "#fff",
                fontSize: "0.75rem",
                "&.Mui-selected": {
                  backgroundColor: "#3C90AD",
                  color: "#fff",
                },
              },
            }}
          >
            <Tab label="GERAL" />
            {mostrarAbaCores && <Tab label="CORES" />}
          </Tabs>

          {/* Conteúdo das Abas — bloqueado quando somente-leitura (B4). */}
          <Box
            sx={{
              flexGrow: 1,
              pb: 4,
              ...(somenteLeitura && { opacity: 0.7, pointerEvents: "none" }),
            }}
          >
            {renderTabContent()}
          </Box>
        </StackCustom>

        {/* Rodapé com Botões de Ação - Fora do container principal.
            FASE 05 — 2ª validação/PO 2026-07-24 (B4): oculto em somente-leitura. */}
        {!somenteLeitura && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mt: 3,
            width: "100%",
          }}
        >
          {isEdicao && (
            <>
              <Button
                variant="text"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setModalExcluirAberto(true)}
                disabled={saving}
              >
                EXCLUIR
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleDuplicar}
                disabled={saving}
                sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}
              >
                DUPLICAR
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSalvar}
            disabled={saving || !alterado}
            sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
          >
            SALVAR
          </Button>
        </Box>
        )}
      </Grid>

      {/* Modais */}

      {/* Modais */}
      <ModalGerenciamentoMidias
        open={modalMidiasAberto}
        onClose={() => setModalMidiasAberto(false)}
        componente={componente}
        onSave={handleMidiasSave}
        onRefreshComponente={carregarComponente}
      />
      <ModalConfirmacaoExcluir
        open={modalExcluirAberto}
        onClose={() => setModalExcluirAberto(false)}
        onConfirm={handleExcluir}
        codigo={componente.codigo || ""}
        descricao={componente.descricao || ""}
      />
      <ModalHistorico
        open={modalHistoricoAberto}
        onClose={() => setModalHistoricoAberto(false)}
        idComponente={idComponente}
      />
    </DashboardStackCustom>
  );
};
